import React from "react";
import { Col, Row } from "reactstrap";
import styled from "styled-components";
import { FormLabel, RequiredIndicator } from "assets/js/Styles";
import CustomSelect from "components/customs/dropdown";
import { addProjectDropdownStyles } from "constants/style";
import { ADD_ACTION, VIEW_ACTION } from "constants/actions";
import FormGenerator from "components/customs/form-generator/input";
import CustomDropzone from "views/projects/add/Dropzone";
import { Toast } from "utility/helpers";
import CustomCheckbox from "components/customs/checkbox";
import { Check } from "react-feather";
import RecipientTable from "./Table";

const SendWithOptions = styled.div`
  display: flex;
  gap: 2rem;
`;

const FormSection = styled.div`
  margin-top: 44px;
  padding-bottom: 4rem;
`;

const RowSection = styled(Row)`
  margin-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  margin-left: 0;
  margin-right: 0;
`;

const CharacterCountSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const FormColRight = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    width: 50%;
  }

  .dropzone-container {
    width: auto;
    height: 65px;
    border: 1px dashed #a3a3a3;

    .upload-files {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
`;

const NewsFormSection = ({
  showForms,
  officeOptions,
  setSelectedOffice,
  selectedOffice,
  action,
  getFilteredRegionOptions,
  handleRegionOnChange,
  selectedRegion,
  form,
  setForm,
  uploadedFiles,
  setUploadedFiles,
  setSelectedAgencies,
  agencyOptions,
  selectedAgencies,
  selectedRecipients,
  setRecipients,
  recipients,
  isCustomSelected,
}) => {
  const handleUploadedFiles = (link) => {
    setUploadedFiles((prevUploadedFiles) => {
      const fileIndex = prevUploadedFiles.findIndex(
        (file) => file.name === link.name
      );

      if (fileIndex !== -1) {
        const updatedFiles = [...prevUploadedFiles];
        updatedFiles[fileIndex] = link;

        return updatedFiles;
      }

      if (prevUploadedFiles.length < 5) {
        return [...prevUploadedFiles, link];
      }

      Toast({
        type: 4,
        content: "Exceeded file upload limit of 5",
      });

      return prevUploadedFiles;
    });
  };

  const handleDeleteFile = (file) => {
    const newUploadedFiles = uploadedFiles.filter(
      (files) => files.name !== file
    );
    setUploadedFiles(newUploadedFiles);
  };

  return (
    <FormSection>
      {showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Office"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Choose your office"}</p>
          </Col>
          <FormColRight>
            <CustomSelect
              placeholder="Office"
              options={officeOptions}
              onChange={(option) => setSelectedOffice(option)}
              styles={addProjectDropdownStyles}
              value={selectedOffice}
              isDisabled={action !== ADD_ACTION}
            />
          </FormColRight>
        </RowSection>
      )}

      {showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Region"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Choose your region"}</p>
          </Col>
          <FormColRight>
            <CustomSelect
              placeholder="Region"
              options={getFilteredRegionOptions()}
              onChange={handleRegionOnChange}
              styles={addProjectDropdownStyles}
              value={selectedRegion}
              isDisabled={action !== ADD_ACTION || !selectedOffice}
              isMulti
            />
          </FormColRight>
        </RowSection>
      )}

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"News Name"}
            {RequiredIndicator}
          </FormLabel>
          {showForms && <p>{"Write the title of your news or announcement"}</p>}
        </Col>
        <FormColRight>
          <FormGenerator
            type="text"
            placeholder="Enter Your Title Here"
            value={form.title}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const {
                target: { value },
              } = event;

              setForm({
                ...form,
                title: value,
              });
            }}
          />
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>{"Upload a File"}</FormLabel>
          {showForms && <p>{"Upload your document"}</p>}
        </Col>
        <FormColRight>
          <CustomDropzone
            handleUploadedFiles={handleUploadedFiles}
            handleDeleteFile={handleDeleteFile}
            uploadedFiles={uploadedFiles}
            label="Upload Files"
            isDisabled={action === VIEW_ACTION}
          />
        </FormColRight>
      </RowSection>

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>{"Description"}</FormLabel>
          {showForms && <p>{"Write your description"}</p>}
        </Col>
        <FormColRight>
          <FormGenerator
            type="textarea"
            placeholder="Enter Text Here"
            rows="5"
            value={form.description}
            isDisabled={action === VIEW_ACTION}
            onChange={(event) => {
              const {
                target: { value },
              } = event;

              setForm({
                ...form,
                description: value,
              });
            }}
          />
        </FormColRight>
      </RowSection>

      {selectedOffice?.value === 2 && showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Agencies"}</FormLabel>
            <p>{"Choose your agencies"}</p>
          </Col>
          <FormColRight>
            <CustomSelect
              isMulti
              placeholder="Agencies"
              onChange={(option) => setSelectedAgencies(option)}
              options={agencyOptions}
              styles={addProjectDropdownStyles}
              value={selectedAgencies}
              isDisabled={action !== ADD_ACTION || !selectedRegion}
            />
          </FormColRight>
        </RowSection>
      )}

      {showForms && (
        <RecipientTable
          data={selectedRecipients}
          setRecipients={setRecipients}
          recipients={recipients}
          action={action}
          isCustomSelected={isCustomSelected}
        />
      )}

      {showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Include Editor/Director"}</FormLabel>
          </Col>
          <FormColRight>
            <SendWithOptions>
              <CustomCheckbox
                checked={form.editor}
                label="Editor"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={(evt) => {
                  const { checked } = evt.target;

                  setForm({
                    ...form,
                    editor: checked,
                  });
                }}
              />

              <CustomCheckbox
                checked={form.director}
                label="Director"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={(evt) => {
                  const { checked } = evt.target;

                  setForm({
                    ...form,
                    director: checked,
                  });
                }}
              />
            </SendWithOptions>
          </FormColRight>
        </RowSection>
      )}

      {showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Include the COS"}</FormLabel>
          </Col>
          <FormColRight>
            <CustomCheckbox
              checked={form.cos}
              label="COS"
              disabled={action !== ADD_ACTION}
              icon={<Check size={16} />}
              onChange={(evt) => {
                const { checked } = evt.target;

                setForm({
                  ...form,
                  cos: checked,
                });
              }}
            />
          </FormColRight>
        </RowSection>
      )}

      {showForms && (
        <RowSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Send with?"}</FormLabel>
          </Col>
          <FormColRight>
            <SendWithOptions>
              <CustomCheckbox
                checked={form.sms}
                label="SMS"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={(evt) => {
                  const { checked } = evt.target;

                  setForm({
                    ...form,
                    sms: checked,
                  });
                }}
              />

              <CustomCheckbox
                checked={form.email}
                label="Email"
                disabled={action !== ADD_ACTION}
                icon={<Check size={16} />}
                onChange={(evt) => {
                  const { checked } = evt.target;

                  setForm({
                    ...form,
                    email: checked,
                  });
                }}
              />
            </SendWithOptions>
          </FormColRight>
        </RowSection>
      )}

      <RowSection>
        <Col
          xs="12"
          md="6"
        >
          <FormLabel>
            {"Message"}
            {RequiredIndicator}
          </FormLabel>
          {showForms && <p>{"Write your message"}</p>}
        </Col>
        <FormColRight>
          <FormGenerator
            type="textarea"
            placeholder="Enter Text Here"
            rows="5"
            value={form.message}
            maxLength={600}
            isDisabled={action !== ADD_ACTION}
            onChange={(event) => {
              const {
                target: { value },
              } = event;

              if (value.length !== 600) {
                setForm({
                  ...form,
                  message: value,
                });
              }
            }}
          />
          <CharacterCountSection>
            {`Characters remaining: ${600 - form.message.length}`}
          </CharacterCountSection>
        </FormColRight>
      </RowSection>
    </FormSection>
  );
};

export default NewsFormSection;
